import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    TextField,
    Button,
    Typography,
    Paper,
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit,
    Info as InfoIcon
} from '@mui/icons-material';
import styles from './ChecklistSetting.module.scss';

export interface ChecklistCategoryProps {
    handleCreateClick: () => void;
    showInput: boolean;
    newCategory: string;
    setNewCategory: (value: string) => void;
    categorysData: { id: number; label: string }[];
    handleAddCategory: () => void;
    editIndex: number | null;
    setEditText: (value: string) => void;
    editText: string;
    handleSaveEdit: (index: number, category: string) => void;
    handleEditClick: (index: number) => void;
    handleDeleteCategoryClick: (index: number) => void;
    handleCancelEdit: () => void; // Added for cancel action
    loading: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
  };
  

export const ChecklistCategory: React.FC<ChecklistCategoryProps> = ({
    handleCreateClick,
    showInput,
    newCategory,
    setNewCategory,
    categorysData,
    handleAddCategory,
    editIndex,
    setEditText,
    editText,
    handleSaveEdit,
    handleEditClick,
    handleDeleteCategoryClick,
    handleCancelEdit,
    loading,
    canEdit = false,
    canDelete = false,
    canExecute = false
  }) => {
    // Track the original value for edit comparison
    const safeEditText = typeof editText === 'string' ? editText : '';
    const originalEditValue = editIndex !== null && categorysData[editIndex] ? categorysData[editIndex].label : '';
    const isUpdateDisabled = !safeEditText.trim() || safeEditText === originalEditValue;
    
    // Character limit validation
    const isNewCategoryAtLimit = newCategory.length >= 200;
    const isEditTextAtLimit = safeEditText.length >= 200;

    // Set editText to the original value when editIndex changes
    useEffect(() => {
        if (editIndex !== null && categorysData[editIndex]) {
            setEditText(categorysData[editIndex].label);
        }
    }, [editIndex]);

    return (
        <div className={styles.leftPanel}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Checklist Category</Typography>
                <Button
                    className={styles.button}
                    onClick={handleCreateClick}
                    disabled={!canExecute}
                    title={canExecute ? 'Add category' : 'You do not have permission to add'}
                    style={{
                        cursor: canExecute ? 'pointer' : 'not-allowed',
                        opacity: canExecute ? 1 : 0.6,
                    }}
                >
                    Add
                </Button>
            </Box>
            {showInput && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                            e.key === 'Enter' && newCategory.trim() && handleAddCategory()
                        }
                        inputProps={{ maxLength: 200 }}
                        error={isNewCategoryAtLimit}
                        helperText={isNewCategoryAtLimit ? 'Maximum 200 characters allowed' : ''}
                        disabled={!canExecute}
                    />
                    <Button
                        className={styles.button}
                        disabled={!canExecute || !newCategory.trim() || editIndex !== null}
                        onClick={handleAddCategory}
                        title={canExecute ? 'Add category' : 'You do not have permission to add'}
                    >
                        Add
                    </Button>
                    <Button
                        className={styles.button}
                        color="secondary"
                        onClick={handleCancelEdit}
                    >
                        Cancel
                    </Button>
                </Box>
            )}
            <div className={styles.tableContainer}>
                <table className={styles.workplaceTable}>
                    <thead>
                        <tr>
                            <th>Category Name</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={20} color="inherit" />
                                </td>
                            </tr>
                        ) : categorysData.length !== 0 ? (
                            categorysData
                                .map((category, index) => (
                                    <tr key={index}>
                                        {editIndex === index ? (
                                            <>
                                                <td colSpan={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                                        <TextField
                                                            value={safeEditText}
                                                            onChange={(e) => setEditText(e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            inputProps={{ maxLength: 200 }}
                                                            error={isEditTextAtLimit}
                                                            helperText={isEditTextAtLimit ? 'Maximum 200 characters allowed' : ''}
                                                        />
                                                        <Button
                                                            onClick={() => handleSaveEdit(index, safeEditText)}
                                                            className={styles.button}
                                                            size="small"
                                                            disabled={!canEdit || isUpdateDisabled}
                                                            title={canEdit ? 'Update category' : 'You do not have permission to edit'}
                                                        >
                                                            Update
                                                        </Button>
                                                        <Button
                                                            onClick={handleCancelEdit}
                                                            className={styles.button}
                                                            color="secondary"
                                                            size="small"
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{category.label}</td>
                                                <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                                    <button
                                                        className={styles.iconButton}
                                                        onClick={() => handleEditClick(index)}
                                                        title={canEdit ? 'Edit' : 'You do not have permission to edit'}
                                                        disabled={!canEdit}
                                                        style={{
                                                            opacity: canEdit ? 1 : 0.5,
                                                            cursor: canEdit ? 'pointer' : 'not-allowed',
                                                        }}
                                                    >
                                                        <Edit className={styles.editIcon} />
                                                    </button>
                                                </td>
                                                <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                                    {categorysData.length > 1 ? (
                                                        <button
                                                            className={styles.iconButton}
                                                            onClick={() => handleDeleteCategoryClick(index)}
                                                            title={canDelete ? 'Delete' : 'You do not have permission to delete'}
                                                            disabled={!canDelete}
                                                            style={{
                                                                opacity: canDelete ? 1 : 0.5,
                                                                cursor: canDelete ? 'pointer' : 'not-allowed',
                                                            }}
                                                        >
                                                            <DeleteIcon className={styles.deleteIcon} />
                                                        </button>
                                                    ) : (
                                                        <Tooltip title="Cannot delete the last category">
                                                            <button
                                                                className={styles.iconButton}
                                                                disabled
                                                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                            >
                                                                <DeleteIcon className={styles.deleteIcon} />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))
                                .reverse()
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>
                                    No records found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
